import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { dataDeletionService } from "@/services/data-deletion-service";
import {
  DeletionJob,
  DeletionJobsQuery,
  DeletionTargetType,
  StartDeletionDto,
  isTerminalDeletionStatus,
} from "@/types/data-deletion";

const DELETION_JOB_POLL_INTERVAL_MS = 2000;
const ACTIVE_TARGET_SCAN_LIMIT = 50;

export const dataDeletionKeys = {
  all: ["data-deletion"] as const,
  jobs: () => [...dataDeletionKeys.all, "jobs"] as const,
  jobList: (query?: DeletionJobsQuery) =>
    [...dataDeletionKeys.jobs(), "list", query ?? {}] as const,
  job: (jobId: string) =>
    [...dataDeletionKeys.jobs(), "detail", jobId] as const,
  activeForTarget: (targetType: DeletionTargetType, targetId: string) =>
    [...dataDeletionKeys.jobs(), "active", targetType, targetId] as const,
};

/**
 * Polls a single deletion job every 2s while it is non-terminal and stops once
 * it reaches `COMPLETED | FAILED | CANCELLED`.
 */
export const useDeletionJob = (jobId: string | null) =>
  useQuery({
    queryKey: dataDeletionKeys.job(jobId ?? "none"),
    queryFn: () => dataDeletionService.getJob(jobId as string),
    enabled: Boolean(jobId),
    staleTime: 0,
    refetchInterval: (query) =>
      isTerminalDeletionStatus(query.state.data?.status)
        ? false
        : DELETION_JOB_POLL_INTERVAL_MS,
  });

export const useDeletionJobs = (query?: DeletionJobsQuery) =>
  useQuery({
    queryKey: dataDeletionKeys.jobList(query),
    queryFn: () => dataDeletionService.getJobs(query),
    // Intentional deviation from the hooks.md 5-min default: the audit log is
    // volatile while wipes run, so use a short staleTime; keepPreviousData keeps
    // prior page data on screen so paging/filtering doesn't flash empty.
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

/**
 * Resolves the in-flight (non-terminal) deletion job for a given target, if any,
 * so a screen can disable its delete button and link to the running job.
 *
 * The list endpoint has no `targetId` filter and `status` accepts a single
 * value, so we scan the most recent {@link ACTIVE_TARGET_SCAN_LIMIT} jobs for
 * this target type client-side. A target with more than that many newer jobs
 * could miss an in-flight one — the backend still enforces single-in-flight and
 * returns 409, which the start flow surfaces, so this is a UX hint, not a guard.
 */
export const useActiveDeletionJobForTarget = (
  targetType: DeletionTargetType,
  targetId: string | null | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: dataDeletionKeys.activeForTarget(targetType, targetId ?? "none"),
    queryFn: async (): Promise<DeletionJob | null> => {
      const response = await dataDeletionService.getJobs({
        targetType,
        limit: ACTIVE_TARGET_SCAN_LIMIT,
      });
      return (
        response.data.find(
          (job) =>
            job.targetId === targetId && !isTerminalDeletionStatus(job.status),
        ) ?? null
      );
    },
    enabled: enabled && Boolean(targetId),
    // Intentional deviation from the hooks.md 5-min default: short staleTime so
    // the in-flight hint refreshes promptly after a wipe starts/finishes (start
    // mutations invalidate this key too).
    staleTime: 15 * 1000,
  });

type StartDeletionVariables = { id: string; dto: StartDeletionDto };
type DeletionStarter = (
  id: string,
  dto: StartDeletionDto,
) => Promise<DeletionJob>;

const useStartDeletionMutation = (starter: DeletionStarter) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: StartDeletionVariables) => starter(id, dto),
    onSuccess: (job, variables) => {
      // Seed the job cache so the status panel can poll immediately.
      queryClient.setQueryData(dataDeletionKeys.job(job.id), job);

      // A guest call without `tenantId` is a HOTEL_ADMIN anonymise. Hotel admins
      // can read a single job (GET /jobs/:jobId) but NOT the jobs list / active-job
      // queries (both SUPER_ADMIN-only), so skip the broad `jobs()` invalidation for
      // them — the seeded detail above is polled directly by the status view.
      const isHotelScopedGuestCall =
        job.targetType === DeletionTargetType.GUEST && !variables.dto.tenantId;
      if (!isHotelScopedGuestCall) {
        queryClient.invalidateQueries({ queryKey: dataDeletionKeys.jobs() });
      }
    },
  });
};

export const useStartTenantDeletion = () =>
  useStartDeletionMutation((id, dto) =>
    dataDeletionService.startTenantDeletion(id, dto),
  );

export const useStartHotelDeletion = () =>
  useStartDeletionMutation((id, dto) =>
    dataDeletionService.startHotelDeletion(id, dto),
  );

export const useStartUserDeletion = () =>
  useStartDeletionMutation((id, dto) =>
    dataDeletionService.startUserDeletion(id, dto),
  );

export const useStartGuestDeletion = () =>
  useStartDeletionMutation((id, dto) =>
    dataDeletionService.startGuestDeletion(id, dto),
  );

export const useCancelDeletionJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => dataDeletionService.cancelJob(jobId),
    onSuccess: (job) => {
      queryClient.setQueryData(dataDeletionKeys.job(job.id), job);
      queryClient.invalidateQueries({ queryKey: dataDeletionKeys.jobs() });
    },
  });
};
